
import { mixins, Component, Watch, Prop, Ref } from 'nuxt-property-decorator';
import SimpleBar from 'simplebar';
import { castQueryArrays } from '../../helpers';
import { GlobalDropdownEvents, InputSelectOption } from '../../types';
import Badge from '../Badge.vue';
import FilterMixin from './FilterMixin';

@Component({
  components: {
    Badge,
  },
})
export default class FilterMultiSelect extends mixins(FilterMixin) {
  @Prop({ default: () => [] }) readonly options!: InputSelectOption[];
  @Prop({ default: '' }) readonly label!: string;
  @Prop({ type: Boolean, default: false }) readonly isMobile!: boolean;

  @Ref() readonly optionsMenu?: HTMLMenuElement;

  isOpen = this.isMobile;
  hasFocus = false;
  simplebarInstance: SimpleBar | null = null;
  scrollElement: HTMLElement | null = null;

  get optionsIdList() {
    return this.options.map(({ id }) => String(id));
  }

  get selected(): string[] {
    if (!this.$route.query[this.queryKey]) return [];
    const query = castQueryArrays(this.$route.query, [this.queryKey]);
    return query[this.queryKey] as string[];
  }

  get selectedLabels(): string {
    const labels = this.selected.map(id => {
      const option = this.options.find(({ id: optionId }) => String(optionId) === id);
      return option ? option.label.toString() : '';
    });
    return labels.filter(x => x).join(', ');
  }

  get ownSelected() {
    return this.selected.filter(id => this.optionsIdList.includes(id));
  }

  @Watch('isOpen')
  onIsOpenChange(isOpen: boolean, wasOpen: boolean) {
    if (isOpen === wasOpen) return;

    if (isOpen) {
      document.body.addEventListener('click', this.closeMenu);
      this.$eventBus.on(GlobalDropdownEvents.Close, this.closeMenu);
    } else {
      document.body.removeEventListener('click', this.closeMenu);
      this.$eventBus.off(GlobalDropdownEvents.Close, this.closeMenu);
    }
  }

  async mounted() {
    await this.$nextTick();
    if (!this.optionsMenu) return;
    this.simplebarInstance = new SimpleBar(this.optionsMenu);
    await this.$nextTick();
    this.scrollElement = this.simplebarInstance.getScrollElement();
    this.optionsMenu?.querySelector('[tabindex="0"]')?.removeAttribute('tabindex');
  }

  beforeDestroy() {
    document.body.removeEventListener('click', this.closeMenu);
    this.$eventBus.off(GlobalDropdownEvents.Close, this.closeMenu);
  }

  public toggleSelection(id: string | number) {
    const normalizedId = String(id);
    const selection = this.selected.includes(normalizedId)
      ? this.selected.filter(selectionId => selectionId !== normalizedId)
      : [...this.selected, normalizedId];

    this.updateQuery(selection, !selection.length);
  }

  public clearOwnSelection() {
    const newSelection = this.selected.filter(id => !this.optionsIdList.includes(id));
    this.updateQuery(newSelection, !newSelection.length);
  }

  public isOptionSelected(id: string | number) {
    return this.selected.includes(String(id));
  }

  public onClickLabel() {
    this.isOpen = !this.isOpen;
    this.$eventBus.emit(GlobalDropdownEvents.Close);
  }

  public closeMenu() {
    this.isOpen = false;
  }

  public onMenuClose() {
    if (!this.scrollElement) return;
    this.scrollElement.scrollTop = 0;
  }
}
